import _ from "lodash";
import React, { useContext, useState } from "react";

import PlanChoices from "./PlanChoices";
import ProductPreview from "./ProductPreview";
import DeviceChoices from "./DeviceChoices";
import PromoCode from "../common/PromoCode";
import PriceView from "../common/PriceView";
import TotalPrice from "../common/TotalPrice";
import ReferralCode from "../common/ReferralCode";
import QuantitySelector from "../common/QuantitySelector";
import { buyNowContext, shippingMethods } from "../BuyNow";
import RecurringPlanPrice from "../common/RecurringPlanPrice";
import { formattedNextChargeDate } from "../../utils/orderHelper";
import {DEVICE_TYPES, PRODUCT_CATEGORIES, PURCHASE_FLOWS} from "../../../../constants";
import { GuaranteesIcons } from "../common/GuaranteesIcons";

/**
 * This product selection component can be used for multiple purchase flows. There is no text specific to ClearStep / Shapa unless it is explicitly coded per the current purchase flow.
 */
const GeneralProductSelection = () => {
  const {
    promoCode,
    appInfo,
    PURCHASE_STAGES,
    selectedPlanId,
    setPurchaseStage,
    shippingStageState,
    setShippingStageState,
    scaleBpBundleProducts,
    scaleProducts,
    planProducts,
    bpDeviceProducts,
    quantitiesEnabled,
    removeLineItems,
    setLineItemsByCategory,
    lineItems,
    purchaseFlow
  } = useContext(buyNowContext);

  const businessName = appInfo.name;

  const deviceLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.SCALE);
  const planLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.MEMBERSHIP);
  const selectedDevice = deviceLineItem ? deviceLineItem.product : null;
  const selectedPlan = planLineItem ? planLineItem.product : null;

  const planDisabled = !!promoCode?.hasLicenseKey;
  const [previewedDevice, setPreviewedDevice] = useState();
  const [alreadyHasDevice, setAlreadyHasDevice] = useState(false);
  const [quantity, setQuantity] = useState(planLineItem ? planLineItem.quantity : 1);
  const isActiveDeviceChoice = !alreadyHasDevice;
  const isActiveMemberShipSection = alreadyHasDevice || selectedDevice;
  const isActiveCheckoutSection = ((alreadyHasDevice && selectedPlan) || selectedDevice) && (selectedPlan || planDisabled);
  const isAdolescentBPDevice = selectedDevice?.name.includes("Adolescent");
  const isBPBundle = selectedDevice ? selectedDevice.deviceType === DEVICE_TYPES.SCALE_BP_BUNDLE : false;
  const isBPDevice = selectedDevice ? selectedDevice.deviceType === DEVICE_TYPES.BP_DEVICE : false;

  let headerText;
  let subHeaderText;
  let productTexts;

  if (purchaseFlow === PURCHASE_FLOWS.SHAPA_END_USER) {
    headerText = "Shapa: The Stress-Free Numberless Scale®";
    subHeaderText = "Lasting results come from habits, not hacks. Your Shapa purchase includes:";
    productTexts = [
      "Color-Based Progress: No numbers. Shapa uses color feedback to track real trends without anxiety.",
      "Daily AI Guidance: Clear insights that keep focus on progress, not weight.",
      "GLP-1 Support: Consistency before, during, or after treatment.",
      isBPDevice || isBPBundle ? "Blood Pressure Benefits: Seamless pairing with Shapa’s monitor for calm, accurate at-home tracking." : null
    ];
  }
  else {
    headerText = `${businessName} numberless scale with monthly plan`;
    subHeaderText = `Going truly numberless. Your ${businessName} purchase includes:`;
    productTexts = [
      `${businessName} ${selectedDevice?.deviceType === DEVICE_TYPES.SCALE_BP_BUNDLE ? `numberless scale and${isAdolescentBPDevice ? " adolescent" : ""} blood pressure monitor` : "numberless scale"}`,
      "Medical-grade, FDA-approved",
      isAdolescentBPDevice ? "Designed for Adolescents (ages 8-19)" : null,
      `Access to ${businessName} app (available in iOS and Android)`,
      `Connection with your team so they can track progress securely through the ${businessName} admin portal`
    ];
  }

  // These product texts are common to all flows, and describe the stats of the selected scale product.
  if (selectedDevice?.maxCapacity)
    productTexts.push(`Max capacity: ${selectedDevice.maxCapacity} LBs`);
  if (selectedDevice?.numSensors)
    productTexts.push(`Equipped with ${selectedDevice.numSensors} sensors for bioelectrical impedance for body composition`);
  if (selectedDevice?.sizeDescription)
    productTexts.push(`${selectedDevice.sizeDescription}`);

  productTexts = productTexts.filter(pt => pt !== null);

  const onAlreadyHasDevice = event => {
    setAlreadyHasDevice(event.target.checked);

    // When this option is either checked or unchecked, let's clear the selected scale and reset the promo code.
    removeLineItems(deviceLineItem);
  };

  const totalPriceDetailsContent = () => {
    if (!isActiveMemberShipSection) {
      return `Please select a ${businessName} scale and a plan`;
    }
    else if (!isActiveCheckoutSection) {
      return "Please select a plan";
    }
    else if (selectedPlan?.recurringPlan) {
      return (
        <span>
          {`Your next payment will start on ${formattedNextChargeDate(selectedPlan, promoCode)} at $`}
          <span>
            {<RecurringPlanPrice />}
          </span>
          {"/month"}
        </span>
      );
    }

    return null;
  };

  const proceedToShipping = () => {
    setPurchaseStage(PURCHASE_STAGES.SHIPPING);
  };

  return (
    <div>
      <div style={{ textAlign: "center" }}>
        <h2 id="buy-now-header" className="col-lg-12 text-lg-center d-lg-block">
          {headerText}
        </h2>
      </div>
      <GuaranteesIcons/>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 col-lg-5 offset-lg-1">
            <ProductPreview product={previewedDevice || selectedDevice || scaleBpBundleProducts[0]} className="py-lg-5" />
          </div>

          <section className="col-lg-5 mt-4">
            <section>
              <h4 className={"offset-1 offset-lg-0 text-dark header-txt"}>
                {subHeaderText}
              </h4>

              <div className="custom-control custom-checkbox my-3 col-10 offset-1 col-lg-12 offset-lg-0 px-0">
                <ul>
                  {productTexts.map((productText, i) => (
                    <li key={i} className='d-flex'>
                      <i className="fa fa-check teal-text icon-size me-1" style={{paddingTop: "2px"}}/>
                      <p className="pb-1 description-txt">
                        {productText}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <section>
              <div className="custom-control custom-checkbox my-3 col-10 offset-1 col-lg-12 offset-lg-0 px-0">
                <h4 className={"text-dark header-txt"}>
                  {`${businessName} products`}
                </h4>

                <div className="description-txt text-muted">Feel secure when stepping on the scale without the anxiety of seeing numbers or having to rely on others.</div>
                {
                  purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_END_USER  && isBPBundle
                  ? <span className="description-txt text-muted">* Scale color may vary between black and oak</span>
                  : null
                }
              </div>

              <div className={"disabled-overlay " + (isActiveDeviceChoice ? 'd-none' : 'd-block')}/>

              <DeviceChoices
                selectedDevice={selectedDevice}
                devices={purchaseFlow === PURCHASE_FLOWS.SHAPA_END_USER ? scaleProducts.concat(scaleBpBundleProducts).concat(bpDeviceProducts) : scaleBpBundleProducts.concat(scaleProducts)}
                onHovered={device => setPreviewedDevice(device)}
                onDeviceSelected={device => {
                  const lineItemUpdates = [];

                  // If a scale doesn't have shipping options available, make sure our state reflects 'standard' shipping.
                  if (!device.shippingOptionsAvailable)
                    setShippingStageState({...shippingStageState, shippingMethod: shippingMethods.standard});

                  lineItemUpdates.push({ product: device, quantity });

                  // Upon selecting a device, let's select the default plan.
                  // If a plan is selected from elsewhere, we'll use that.
                  if (!selectedPlan) {
                    if (selectedPlanId) {
                      const indexOfSelectedPlan = planProducts.findIndex((plan) => plan.id === parseInt(selectedPlanId));
                      lineItemUpdates.push({ product: planProducts[indexOfSelectedPlan], quantity });
                    } else
                      lineItemUpdates.push({ product: planProducts.find(p => p.default), quantity });
                  }

                  setLineItemsByCategory(lineItemUpdates);
                }}
              />
            </section>

            <div className="form-check d-lg-flex align-items-lg-center col-10 offset-1 px-4 col-lg-12 offset-lg-0">
              <input
                type="checkbox" className="form-check-input me-2" id="checkboxDevices"
                defaultChecked={alreadyHasDevice}
                name="checkboxDevices" onClick={event => onAlreadyHasDevice(event)}
              />
              <label className="form-check-label plan-main-txt fw-bolder pt-lg-1" htmlFor="checkboxDevices">
                {`I already have ${businessName} products`}
              </label>
            </div>
          </section>
        </div>

        <div className="row row mt-4">
          <div className="col-12 col-lg-5 offset-lg-1">

          </div>

          <section className="col-lg-5">
            <div className={"disabled-overlay " + (isActiveMemberShipSection ? 'd-none' : 'd-block')}/>
            <div className="offset-1 col-10 px-0 offset-lg-0 col-lg-12">
              {!planDisabled &&
                <div>
                  <h4 className="text-dark header-txt mt-0">Monthly plan</h4>

                  <span className="description-txt text-muted">
                    A monthly plan connects you to your treatment team through our 100% HIPAA-compliant platform with strong encryption.
                  </span>

                  <section className="my-1">
                    <PlanChoices
                      selectedPlan={selectedPlan}
                      plans={planProducts}
                      onPlanSelected={plan => { setLineItemsByCategory([{ product: plan, quantity }]); }}
                    />

                    {!_.isEmpty(selectedPlan) && selectedPlan.recurringPlan &&
                      <div>
                          <span className="description-txt text-muted">
                            {"Starting today, you will automatically be charged $"}
                            <span className="description-txt text-muted">{<RecurringPlanPrice />}</span>
                            {"/month. You can cancel anytime before your next billing cycle."}
                          </span>
                      </div>
                    }
                  </section>
                </div>
              }

              <br/>

              {quantitiesEnabled &&
                <div className="mb-5 fw-bold">
                  <h4 className="text-dark header-txt">Quantity</h4>
                  <QuantitySelector quantityValue={quantity} onChange={val => {
                    setQuantity(val);

                    const lineItemUpdates = [];
                    if (selectedDevice) lineItemUpdates.push({ product: selectedDevice, quantity: val });
                    if (selectedPlan) lineItemUpdates.push({ product: selectedPlan, quantity: val });

                    setLineItemsByCategory(lineItemUpdates);
                  }}/>
                </div>
              }

              <PromoCode />
              <ReferralCode />
              <PriceView hideDecimalIfWhole={true} includeShippingCost={false} className="text-center" />
            </div>

            <div className="my-5 text-center">
              <div className={"disabled-overlay pay-btn-section " + (isActiveCheckoutSection ? 'd-none' : 'd-block')}>
              </div>

              <p className="d-block pb-0 checkout-sub-txt">Subtotal</p>
              <h1 className="d-block py-2 display-4 fw-bold total-price-txt">${<TotalPrice hideDecimalIfWhole={true} includeShippingCost={false} />}</h1>
              <p className="d-block checkout-sub-txt">
                {totalPriceDetailsContent()}
              </p>

              <div className="text-center py-3">
                <a className="teal button-link next-button-txt next-step-button" onClick={() => { proceedToShipping(); }}>
                  Proceed to checkout
                </a>
              </div>

              <div className="mt-3 text-center">
                <p className="text-muted font-italic most-popular">
                  Sales tax and shipping will be calculated at checkout
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default GeneralProductSelection;
